import { useEffect, useRef, useState } from 'react';

/** A multi-select report filter: the chosen codes and whether they are excluded instead of included. */
export interface FilterSelection {
  codes: string[];
  exclude: boolean;
}

/** Same codes (in any order) and the same include/exclude mode. */
export const isSameFilterSelection = (a: FilterSelection, b: FilterSelection): boolean =>
  a.exclude === b.exclude && a.codes.length === b.codes.length && a.codes.every((code) => b.codes.includes(code));

export interface PendingFilterSelection {
  pending: FilterSelection;
  toggleCode: (code: string) => void;
  // Selects every given code, keeping those already selected ("Select all" over a filtered list).
  addCodes: (codes: string[]) => void;
  setExclude: (exclude: boolean) => void;
  clearCodes: () => void;
}

/**
 * A draft of a filter popover's selection. Ticks edit only the draft; it is committed once, when the
 * popover closes (Apply, an outside click, its trigger, or another popover opening), and only if it
 * changed -- so a report's fetch effect, which depends on the applied selection, runs once per popover
 * session instead of once per checkbox. While closed, the draft follows the applied selection, so a
 * loaded saved view or a filter reset shows up the next time the popover opens.
 */
export function usePendingFilterSelection(
  applied: FilterSelection,
  isOpen: boolean,
  commit: (next: FilterSelection) => void
): PendingFilterSelection {
  const [pending, setPending] = useState<FilterSelection>(applied);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (isOpen) return;
    if (wasOpen && !isSameFilterSelection(pendingRef.current, applied)) {
      commitRef.current(pendingRef.current);
      return;
    }
    // Keeping prev when equal bails out of the re-render, since `applied` is a fresh object each render.
    setPending((prev) => (isSameFilterSelection(prev, applied) ? prev : applied));
  }, [isOpen, applied]);

  return {
    pending,
    toggleCode: (code) =>
      setPending((prev) => ({
        ...prev,
        codes: prev.codes.includes(code) ? prev.codes.filter((c) => c !== code) : [...prev.codes, code],
      })),
    addCodes: (codes) =>
      setPending((prev) => ({ ...prev, codes: [...prev.codes, ...codes.filter((code) => !prev.codes.includes(code))] })),
    setExclude: (exclude) => setPending((prev) => ({ ...prev, exclude })),
    clearCodes: () => setPending((prev) => ({ ...prev, codes: [] })),
  };
}
